import {
  Box,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  IconButton,
  Image,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Text as ChakraText,
  Thead,
  Tr,
  VStack,
} from '@chakra-ui/react';
import type {
  CollectionMutationDetailResponse,
  CollectionPaymentProofDetailResponse,
} from '@diamond/shared/types';
import { formatCurrencyIdr } from '@diamond/shared/utils';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { useState } from 'react';

import {
  BodyCell,
  HeaderCell,
  LabelText,
  SectionTitle,
  ValueText,
} from '../../../components/mutation-drawer-typography';
import type {
  LHCDetailInvoiceRowData,
  SearchNomorMutasiRowData,
} from '../../../types';
import { getMutationDetailDrawerViewModel } from './mutation-detail-drawer.helpers';

interface MutationDetailDrawerProps {
  isOpen: boolean;
  selectedMutation: SearchNomorMutasiRowData | null;
  rowData: LHCDetailInvoiceRowData | null;
  mutationDetailData?: CollectionMutationDetailResponse | null;
  paymentProofDetailData?: CollectionPaymentProofDetailResponse | null;
  isLoading?: boolean;
  onClose: () => void;
}

interface ImagePlaceholderProps {
  message: string;
}

function ImagePlaceholder({ message }: ImagePlaceholderProps) {
  return (
    <Flex
      minH="240px"
      minW="270px"
      align="center"
      justify="center"
      bg="#F2F2F7"
      borderRadius="md"
      color="#8E8E93"
      fontSize="14px"
    >
      {message}
    </Flex>
  );
}

const transactionHeaders = [
  'No LHC',
  'No Surat Jalan',
  'Faktur',
  'Plant',
  'Nilai SO',
] as const;

export function MutationDetailDrawer({
  isOpen,
  selectedMutation,
  rowData,
  mutationDetailData,
  paymentProofDetailData,
  isLoading = false,
  onClose,
}: MutationDetailDrawerProps) {
  const viewModel = getMutationDetailDrawerViewModel({
    mutationDetailData,
    paymentProofDetailData,
    rowData,
    selectedMutation,
  });
  const [imageError, setImageError] = useState(false);

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose}>
      <DrawerOverlay zIndex={1400} />
      <DrawerContent zIndex={1401} w="768px" maxW="768px">
        <DrawerHeader p={4}>
          <Flex align="center" gap={2}>
            <IconButton
              aria-label="Kembali"
              icon={<ArrowBackIosNewIcon fontSize="small" />}
              variant="ghost"
              size="sm"
              onClick={onClose}
            />
            <ChakraText fontWeight={500} fontSize="20px" color="#373F41">
              Mutasi & Bukti Transfer
            </ChakraText>
          </Flex>
        </DrawerHeader>
        <DrawerCloseButton zIndex={1402} />
        <DrawerBody p={6}>
          {isLoading ? (
            <Flex minH="320px" align="center" justify="center">
              <Spinner size="lg" />
            </Flex>
          ) : viewModel.hasContent ? (
            <VStack spacing={6} align="stretch">
              <Flex justify="space-between" gap={4}>
                <Box flex={1}>
                  <LabelText mb={1}>Nomor Mutasi</LabelText>
                  <ValueText color="blue.600">
                    {viewModel.mutationNumber}
                  </ValueText>
                </Box>
                <Box flex={1}>
                  <LabelText mb={1}>Total PO</LabelText>
                  <ValueText>{viewModel.poAmount}</ValueText>
                </Box>
                <Box flex={1}>
                  <LabelText mb={1}>Over Payment / Deposit</LabelText>
                  <ValueText>{viewModel.overPayment}</ValueText>
                </Box>
              </Flex>

              <Flex justify="space-between" gap={4}>
                <Box flex={1}>
                  <LabelText mb={1}>Purchase Order</LabelText>
                  <ValueText mb={2}>{viewModel.poNumber}</ValueText>
                </Box>
                <Box flex={1}>
                  <LabelText mb={1}>Rekap PO</LabelText>
                  <VStack align="start" spacing={1}>
                    <ChakraText fontSize="14px" color="#373F41">
                      {viewModel.relatedTransactions.length} Transaksi terkait
                    </ChakraText>
                    <ChakraText fontSize="14px" color="#373F41">
                      {viewModel.orderPaymentCount} Pembayaran
                    </ChakraText>
                    <ChakraText fontSize="14px" color="#373F41">
                      {viewModel.paymentProofCount} Bukti transfer
                    </ChakraText>
                  </VStack>
                </Box>
                <Box flex={1} />
              </Flex>

              <Box>
                <SectionTitle mb={3}>Transaksi Terkait</SectionTitle>
                <TableContainer
                  border="1px solid"
                  borderColor="#E5E5EA"
                  borderRadius="8px"
                  overflow="hidden"
                >
                  <Table variant="simple" size="sm">
                    <Thead>
                      <Tr bg="#E5E5EA !important">
                        {transactionHeaders.map((header) => (
                          <HeaderCell key={header}>{header}</HeaderCell>
                        ))}
                      </Tr>
                    </Thead>
                    <Tbody>
                      {viewModel.relatedTransactions.length ? (
                        viewModel.relatedTransactions.map((transaction) => (
                          <Tr key={transaction.transaction_id}>
                            <BodyCell>{transaction.lhc_no || '-'}</BodyCell>
                            <BodyCell>
                              {transaction.delivery_order_number || '-'}
                            </BodyCell>
                            <BodyCell>
                              {transaction.billing_number || '-'}
                            </BodyCell>
                            <BodyCell>
                              {transaction.delivery_order_plant_name || '-'}
                            </BodyCell>
                            <BodyCell>
                              {formatCurrencyIdr(transaction.so_amount)}
                            </BodyCell>
                          </Tr>
                        ))
                      ) : (
                        <Tr>
                          <BodyCell colSpan={5}>
                            Belum ada transaksi terkait
                          </BodyCell>
                        </Tr>
                      )}
                    </Tbody>
                  </Table>
                </TableContainer>
              </Box>

              <Box>
                <SectionTitle mb={3}>Bukti Transfer</SectionTitle>
                <Box p={4}>
                  <Flex gap={6} align="flex-start">
                    {viewModel.proofImageUrl && !imageError ? (
                      <Image
                        src={viewModel.proofImageUrl}
                        alt="Bukti Transfer"
                        maxW="270px"
                        borderRadius="md"
                        objectFit="contain"
                        onError={() => setImageError(true)}
                      />
                    ) : (
                      <ImagePlaceholder
                        message={
                          imageError
                            ? 'Gagal memuat gambar'
                            : 'Tidak ada bukti transfer'
                        }
                      />
                    )}

                    <VStack align="stretch" spacing={6} flex={1}>
                      <Flex gap={8}>
                        <Box flex={1}>
                          <LabelText mb={1}>Purchase Order</LabelText>
                          <ValueText fontSize="16px">
                            {viewModel.poNumber}
                          </ValueText>
                        </Box>
                        <Box flex={1}>
                          <LabelText mb={1}>Nomor Rekening</LabelText>
                          <ValueText fontSize="16px">
                            {viewModel.bankAccount}
                          </ValueText>
                        </Box>
                      </Flex>

                      <Flex gap={8}>
                        <Box flex={1}>
                          <LabelText mb={1}>Jumlah Bukti</LabelText>
                          <ValueText fontSize="16px" wordBreak="break-word">
                            {String(viewModel.paymentProofCount)}
                          </ValueText>
                        </Box>
                        <Box flex={1}>
                          <LabelText mb={1}>Bank</LabelText>
                          <ValueText fontSize="16px">
                            {viewModel.bankName}
                          </ValueText>
                        </Box>
                      </Flex>
                    </VStack>
                  </Flex>
                </Box>
              </Box>
            </VStack>
          ) : null}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}
